<template>
  <div class="pa-1 mt-4">
    <!-- Toolbar -->
    <v-row align="center" class="mb-2">
      <v-col cols="auto">
        <v-checkbox
          v-model="groupByEmployee"
          :label="$t('group_by_employee')"
          hide-details
          dense
          class="mt-0"
        ></v-checkbox>
      </v-col>
    </v-row>

    <!-- Desktop View -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="groupByEmployee ? groupedHeaders : headers"
      :items="groupByEmployee ? groupedTopupsWithTotal : creditTopupsWithTotal"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-cash-off</v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>

      <template v-slot:item.num="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">{{ item.num }}</span>
      </template>

      <template v-slot:item.date="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ item.isTotal ? $t("total").toUpperCase() : localeDate(item.date) }}
        </span>
      </template>

      <template v-slot:item.user_name="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">{{ toLatin(item.user_name) }}</span>
      </template>

      <template v-slot:item.restaurant_label="{ item }">
        <span v-if="!item.isTotal" :class="item.restaurant_id ? '' : 'text--secondary'">
          {{ item.restaurant_id ? toLatin(item.restaurant_name) : $t('all_restaurants') }}
        </span>
      </template>

      <template v-slot:item.total_amount="{ item }">
        <span
          :class="
            item.isTotal ? 'font-weight-bold primary--text' : 'font-weight-bold success--text'
          "
        >
          {{ formatAmount(item.total_amount) }}
        </span>
      </template>

      <!-- Grouped view slots -->
      <template v-slot:item.topup_count="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">{{ item.topup_count }}</span>
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="groupByEmployee ? groupedMobileHeaders : mobileHeaders"
      :items="groupByEmployee ? groupedTopups : creditTopups"
      :loading="loading"
      item-key="id"
      :fields-per-row="2"
      :bold-fields="['user_name']"
      :show-row-number="false"
      class="px-3"
    >
      <template v-slot:item.date="{ item }">
        {{ localeDate(item.date) }}
      </template>
      <template v-slot:item.restaurant_label="{ item }">
        <span :class="item.restaurant_id ? '' : 'text--secondary'">
          {{ item.restaurant_id ? toLatin(item.restaurant_name) : $t('all_restaurants') }}
        </span>
      </template>
      <template v-slot:item.total_amount="{ item }">
        <span class="font-weight-bold success--text">
          {{ formatAmount(item.total_amount) }}
        </span>
      </template>
    </mobile-data-cards>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import limitService from "@/services/limit";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabCreditTopups",
  components: {
    MobileDataCards,
  },
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    creditTopups: [],
    loading: false,
    groupByEmployee: false,
  }),
  computed: {
    filters() {
      return this.$store.state.report.filters;
    },
    dates() {
      const { from, to } = this.$store.state.report.filters.dates;
      return { from, to };
    },
    companyId() {
      return this.$store.state.company?.id || null;
    },
    grandTotal() {
      return this.creditTopups.reduce((sum, t) => sum + t.total_amount, 0);
    },
    groupedTopups() {
      const byUser = {};
      this.creditTopups.forEach((t) => {
        const key = t.user_name || "";
        if (!byUser[key]) {
          byUser[key] = { user_name: t.user_name, topup_count: 0, total_amount: 0 };
        }
        byUser[key].topup_count += 1;
        byUser[key].total_amount += t.total_amount;
      });
      return Object.values(byUser).sort((a, b) =>
        a.user_name.localeCompare(b.user_name)
      );
    },
    groupedTopupsWithTotal() {
      const rows = this.groupedTopups.map((item, index) => ({
        ...item,
        num: index + 1,
        isTotal: false,
      }));
      if (rows.length > 0) {
        rows.push({
          id: "total",
          num: "",
          user_name: "",
          topup_count: rows.reduce((s, r) => s + r.topup_count, 0),
          total_amount: this.grandTotal,
          isTotal: true,
        });
      }
      return rows;
    },
    groupedHeaders() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "80px", print: false },
        { text: this.$t("employee"), value: "user_name", sortable: true },
        { text: this.$t("count"), value: "topup_count", sortable: true, align: "center" },
        {
          text: this.$t("amount") + ` (${this.restaurantCurrency})`,
          value: "total_amount",
          sortable: true,
          align: "right",
        },
      ];
    },
    groupedMobileHeaders() {
      return [
        { text: this.$t("employee"), value: "user_name" },
        { text: this.$t("count"), value: "topup_count" },
        { text: this.$t("amount") + ` (${this.restaurantCurrency})`, value: "total_amount" },
      ];
    },
    creditTopupsWithTotal() {
      const topups = this.creditTopups.map((item, index) => ({
        ...item,
        num: index + 1,
        isTotal: false,
      }));
      if (topups.length > 0) {
        topups.push({
          id: "total",
          num: "",
          date: "",
          user_name: "",
          total_amount: this.grandTotal,
          isTotal: true,
        });
      }
      return topups;
    },
    headers() {
      return [
        {
          text: this.$t("r_b"),
          value: "num",
          sortable: false,
          width: "80px",
          print: false,
        },
        { text: this.$t("date"), value: "date", sortable: true },
        { text: this.$t("employee"), value: "user_name", sortable: false },
        { text: this.$t("restaurant"), value: "restaurant_label", sortable: false },
        {
          text: this.$t("amount") + ` (${this.restaurantCurrency})`,
          value: "total_amount",
          sortable: false,
          align: "right",
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("date"), value: "date" },
        { text: this.$t("employee"), value: "user_name" },
        { text: this.$t("restaurant"), value: "restaurant_label" },
        {
          text: this.$t("amount") + ` (${this.restaurantCurrency})`,
          value: "total_amount",
        },
      ];
    },
  },
  methods: {
    parseAmount(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    normalizeCreditTransaction(item) {
      const userName = [item?.user?.name, item?.user?.lastname]
        .filter(Boolean)
        .join(" ");
      return {
        id: item?.id || null,
        date: item?.date || item?.created_at || null,
        user_name: this.toLatin(userName || item?.user_name || ""),
        restaurant_id: item?.restaurant_id ?? null,
        restaurant_name: item?.restaurant?.name || "",
        total_amount: this.parseAmount(item?.total_amount),
        notes: item?.notes || "",
      };
    },
    async fetchTopups() {
      if (!this.companyId) return;
      this.loading = true;
      this.creditTopups = [];
      try {
        const { data } = await limitService.getCompanyTransactions(this.companyId, {
          date: this.dates.from,
          date2: this.dates.to,
        });
        const userGroups = Array.isArray(data) ? data : [];
        this.creditTopups = userGroups
          .flatMap((group) => group.limitTransactions || [])
          .filter((t) => {
            if (t.orderable_id !== null) return false;
            if (t.company_id !== this.companyId) return false;
            if (this.parseAmount(t.total_amount) <= 0) return false;
            const txDate = (t.date || "").substring(0, 10);
            return txDate >= this.dates.from && txDate <= this.dates.to;
          })
          .map((t) => this.normalizeCreditTransaction(t))
          .sort((a, b) => new Date(b.date) - new Date(a.date));
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    formatAmount(amount) {
      return (parseFloat(amount) || 0).toFixed(2);
    },
    getExportHeaders() {
      const h = this.groupByEmployee ? this.groupedHeaders : this.headers;
      return h.filter((header) => header.print !== false);
    },
    formatTopupForExport(item) {
      const exportItem = {};
      this.getExportHeaders().forEach((h) => {
        if (h.value === "date") exportItem[h.value] = this.localeDate(item.date);
        else if (h.value === "restaurant_label")
          exportItem[h.value] = item.restaurant_id
            ? this.toLatin(item.restaurant_name)
            : this.$t("all_restaurants");
        else if (h.value === "total_amount")
          exportItem[h.value] = this.formatAmount(item.total_amount);
        else exportItem[h.value] = item[h.value] || "";
      });
      return exportItem;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.fetchTopups();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      const exportItems = this.groupByEmployee
        ? this.groupedTopups.map((item, i) => ({
            num: i + 1,
            user_name: this.toLatin(item.user_name),
            topup_count: item.topup_count,
            total_amount: this.formatAmount(item.total_amount),
          }))
        : this.creditTopups.map((item) => this.formatTopupForExport(item));
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("credit_topups")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("credit_topups"),
            headers: this.getExportHeaders(),
            items: exportItems,
            title: [this.$t("report"), this.$t("credit_topups")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      const printItems = this.groupByEmployee
        ? this.groupedTopups.map((item, i) => ({
            num: i + 1,
            user_name: this.toLatin(item.user_name),
            topup_count: item.topup_count,
            total_amount: this.formatAmount(item.total_amount),
          }))
        : this.creditTopups.map((item) => this.formatTopupForExport(item));
      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: printItems,
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("credit_topups")].join(": "),
        subtitle: this.subtitle,
        hideDatetime: true,
      });
    },
  },
  mounted() {
    this.fetchTopups();
  },
};
</script>

